import { useState } from 'react'
import { useAuth } from '@/contexts/AuthProvider'
import { useMobileCheckin } from '@/hooks/useMobileCheckin'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, LogIn, LogOut, Clock, MapPin } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { toast } from 'sonner'

export default function CheckInPage() {
  const { userProfile, company } = useAuth()
  const { currentStatus, isClockedIn, recentHistory, siteLocations, loading, clockIn, clockOut } = useMobileCheckin(userProfile?.id, company?.id)
  const [acting, setActing] = useState(false)
  const [selectedSiteId, setSelectedSiteId] = useState('')

  const selectedSite = siteLocations.find(s => s.id === selectedSiteId)

  const handleClockIn = async () => {
    if (!selectedSiteId) {
      toast.error('Please select a site first')
      return
    }

    setActing(true)
    try {
      let coords = null
      if (navigator.geolocation) {
        try {
          const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 })
          })
          coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }
        } catch {
          // Geolocation not available or denied
        }
      }

      const result = await clockIn(selectedSite, coords)
      if (result?.offline) {
        toast.info('Clock-in queued — will sync when online')
      } else {
        toast.success('Clocked in successfully')
      }
    } catch (err) {
      toast.error(err.message || 'Failed to clock in')
    } finally {
      setActing(false)
    }
  }

  const handleClockOut = async () => {
    setActing(true)
    try {
      const result = await clockOut()
      if (result?.offline) {
        toast.info('Clock-out queued — will sync when online')
      } else {
        toast.success('Clocked out successfully')
      }
    } catch (err) {
      toast.error(err.message || 'Failed to clock out')
    } finally {
      setActing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    )
  }

  const duration = isClockedIn && currentStatus?.check_in_time
    ? formatDistanceToNow(new Date(currentStatus.check_in_time))
    : null

  return (
    <div className="p-4 space-y-5">
      <h1 className="text-xl font-bold text-gray-900">Time & Check-In</h1>

      {/* Status + Action */}
      <div className={`rounded-2xl border-2 p-6 text-center ${isClockedIn ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-gray-200'}`}>
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isClockedIn ? 'bg-emerald-500' : 'bg-gray-200'}`}>
          {isClockedIn ? (
            <LogIn className="h-8 w-8 text-white" />
          ) : (
            <Clock className="h-8 w-8 text-gray-400" />
          )}
        </div>

        <p className="text-lg font-bold text-gray-900 mb-1">
          {isClockedIn ? 'You are clocked in' : 'Not clocked in'}
        </p>

        {isClockedIn && currentStatus?.site_location?.name && (
          <p className="text-sm text-gray-500 flex items-center justify-center gap-1 mb-1">
            <MapPin className="h-3.5 w-3.5" />
            {currentStatus.site_location.name}
          </p>
        )}

        {duration && (
          <p className="text-sm text-gray-400 mb-4">{duration}</p>
        )}

        {/* Site Selection (only when not clocked in) */}
        {!isClockedIn && (
          <div className="mb-4">
            {siteLocations.length > 0 ? (
              <Select value={selectedSiteId} onValueChange={setSelectedSiteId}>
                <SelectTrigger className="w-full h-12 rounded-xl text-sm">
                  <SelectValue placeholder="Select a site..." />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4} className="max-h-[50vh] w-[var(--radix-select-trigger-width)]">
                  {siteLocations.map(site => (
                    <SelectItem key={site.id} value={site.id} className="py-3 text-sm">
                      {site.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-sm text-gray-500">No sites available</p>
                <p className="text-xs text-gray-400 mt-0.5">Ask your manager to configure site locations.</p>
              </div>
            )}
          </div>
        )}

        <Button
          size="lg"
          className={`w-full text-base ${isClockedIn ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}
          onClick={isClockedIn ? handleClockOut : handleClockIn}
          disabled={acting || (!isClockedIn && !selectedSiteId)}
        >
          {acting ? (
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
          ) : isClockedIn ? (
            <LogOut className="h-5 w-5 mr-2" />
          ) : (
            <LogIn className="h-5 w-5 mr-2" />
          )}
          {isClockedIn ? 'Clock Out' : 'Clock In'}
        </Button>
      </div>

      {/* Recent History */}
      <div>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">Last 7 Days</h2>
        {recentHistory.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-gray-400 text-sm">
            No check-ins in the last 7 days.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {recentHistory.map(c => {
              const inTime = new Date(c.check_in_time)
              const outTime = c.check_out_time ? new Date(c.check_out_time) : null
              const durationMs = outTime ? outTime - inTime : null
              const hours = durationMs ? Math.floor(durationMs / 3600000) : null
              const mins = durationMs ? Math.floor((durationMs % 3600000) / 60000) : null

              return (
                <div key={c.id} className="px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {inTime.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                      </p>
                      <p className="text-xs text-gray-500">
                        {c.site_location?.name || 'Unknown'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900">
                        {inTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        {outTime && (
                          <> — {outTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</>
                        )}
                      </p>
                      {hours !== null ? (
                        <p className="text-xs text-gray-400">{hours}h {mins}m</p>
                      ) : (
                        <p className="text-xs text-emerald-600 font-medium">Active</p>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
